/**
 * OsLabels — OS-INV-13
 *
 * Route: /os/labels  (staff-authenticated via OffshoreProtectedRoute)
 *
 * Label layout rationale:
 *   - Letter paper: 215.9mm × 279.4mm
 *   - Label size: 60mm × 35mm
 *   - Grid math: 3 columns × 7 rows = 21 labels/sheet
 *       • Col margin: (215.9 - 3×60) / 2 = 17.95mm each side
 *       • Row margin: top 5mm, bottom = 279.4 - 5 - 7×35 = 29.4mm
 *   - Cut lines: CSS border on labels with gap:0 so borders form the guillotine grid.
 *     The outer wrapper has NO border so the edge labels bleed cleanly to the margin.
 *   - Each label: QR in left ~22mm, text in remaining ~38mm
 *   - TWO labels per item when both locations have stock > 0.
 *   - Print All / Select All: only includes location buckets with stock > 0.
 *   - Manual single print: always available for any item/location regardless of stock.
 *
 * Print accuracy: uses mm units throughout @media print. page-break-inside: avoid
 * is set per label. -webkit-print-color-adjust: exact ensures borders print.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import QRCode from 'qrcode';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';

// ── Types ──────────────────────────────────────────────────────────────────

interface MaterialRow {
  id: string;
  numeric_id?: number;
  name?: string;
  description?: string;
  unit?: string;
  stock_storeroom: number;
  stock_working: number;
  // OS-INV-17: per-location item numbers from the mapping
  locationItemNumbers?: { storeroom: number | null; working: number | null };
}

type LocationKey = 'working' | 'storeroom';

interface LabelItem {
  /** Unique key for selection: "${material.id}::${location}" */
  key: string;
  materialId: string;
  numericId?: number;
  name: string;
  unit?: string;
  location: LocationKey;
  stock: number;
  qrUrl: string;
  qrDataUrl: string;
  /** OS-INV-17: the per-location item number for this material+location (or null) */
  itemNumber: number | null;
}

// ── QR helper ──────────────────────────────────────────────────────────────

async function generateQr(url: string): Promise<string> {
  return QRCode.toDataURL(url, { width: 88, margin: 1, errorCorrectionLevel: 'M' });
}

// ── Component ──────────────────────────────────────────────────────────────

export const OsLabels: React.FC = () => {
  const { apiBase, routeBase: _routeBase } = useTenant();
  const [labels, setLabels]           = useState<LabelItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [selectMode, setSelectMode]   = useState(false);
  const [selected, setSelected]       = useState<Set<string>>(new Set());
  const [filter, setFilter]           = useState('');
  const [showStockedOnly, setShowStockedOnly] = useState(true);
  const printRef = useRef<HTMLDivElement>(null);

  // ── Load materials + build label list ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await authFetch(`${apiBase}/materials`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const rows: MaterialRow[] = (data.materials || data || []);

        const items: LabelItem[] = [];
        for (const m of rows) {
          const name = m.name || m.description || m.id;
          const origin = window.location.origin;
          for (const loc of ['working', 'storeroom'] as LocationKey[]) {
            const stock = loc === 'working' ? (m.stock_working ?? 0) : (m.stock_storeroom ?? 0);
            const itemNumber = m.locationItemNumbers ? (m.locationItemNumbers[loc] ?? null) : null;
            // OS-INV-17: QR encodes the per-location item number when present, so a
            // scan resolves directly to (material, location). Falls back to the
            // material-id scan URL for un-numbered locations (back-compat).
            const qrUrl = itemNumber != null
              ? `${origin}/os/scan/${itemNumber}/${loc}`
              : `${origin}/os/scan/${encodeURIComponent(m.id)}/${loc}`;
            let qrDataUrl = '';
            try { qrDataUrl = await generateQr(qrUrl); } catch { /* ignore */ }
            items.push({
              key:        `${m.id}::${loc}`,
              materialId: m.id,
              numericId:  m.numeric_id,
              name,
              unit:       m.unit,
              location:   loc,
              stock,
              qrUrl,
              qrDataUrl,
              itemNumber,
            });
          }
        }

        // Sort by numeric_id ascending then name
        items.sort((a, b) => {
          if (a.numericId !== undefined && b.numericId !== undefined) return a.numericId - b.numericId;
          if (a.numericId !== undefined) return -1;
          if (b.numericId !== undefined) return 1;
          return a.name.localeCompare(b.name);
        });

        if (!cancelled) setLabels(items);
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error).message ?? 'Failed to load materials');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filterLower = filter.toLowerCase();
  const filtered = labels.filter(l => {
    const matchesText =
      !filter ||
      l.name.toLowerCase().includes(filterLower) ||
      l.materialId.toLowerCase().includes(filterLower) ||
      String(l.numericId ?? '').includes(filterLower) ||
      String(l.itemNumber ?? '').includes(filterLower);
    const matchesStock = !showStockedOnly || l.stock > 0;
    return matchesText && matchesStock;
  });

  // Labels eligible for "Print All" (stock > 0 for that bucket)
  const printableAll = filtered.filter(l => l.stock > 0);

  // ── Selection ──────────────────────────────────────────────────────────────
  const toggleSelect = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(printableAll.map(l => l.key)));
  const deselectAll = () => setSelected(new Set());

  // ── Print ──────────────────────────────────────────────────────────────────
  const printLabels = (labelsToPrint: LabelItem[]) => {
    // Write print-only content into a hidden div then trigger print
    const container = printRef.current;
    if (!container) return;
    // Inject into container then window.print()
    // The container is rendered visibly on-screen in print mode via @media print
    // We store which labels to print in a data attribute and CSS shows/hides
    container.dataset.printKeys = JSON.stringify(labelsToPrint.map(l => l.key));
    // Mark labels for printing
    const allLabelEls = container.querySelectorAll<HTMLElement>('[data-label-key]');
    allLabelEls.forEach(el => {
      el.style.display = labelsToPrint.some(l => l.key === el.dataset.labelKey) ? '' : 'none';
    });
    window.print();
    // Restore
    allLabelEls.forEach(el => { el.style.display = ''; });
  };

  const handlePrintAll  = () => printLabels(printableAll);
  const handlePrintSel  = () => printLabels(filtered.filter(l => selected.has(l.key)));
  const handlePrintOne  = (l: LabelItem) => printLabels([l]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, '@media print': { p: 0 } }}>
      {/* Controls — hidden on print */}
      <Box
        className="no-print"
        sx={{
          mb: 3,
          '@media print': { display: 'none' },
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight={700}>Inventory Labels</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintAll}
              disabled={loading || printableAll.length === 0}
              sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
            >
              Print All ({printableAll.length})
            </Button>
            <Button
              variant="outlined"
              startIcon={selectMode ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              onClick={() => { setSelectMode(m => !m); deselectAll(); }}
            >
              {selectMode ? 'Cancel Select' : 'Select'}
            </Button>
            {selectMode && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrintSel}
                disabled={selected.size === 0}
              >
                Print Selected ({selected.size})
              </Button>
            )}
          </Stack>
        </Stack>

        {selectMode && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={selectAll}>Select All ({printableAll.length})</Button>
            <Button size="small" onClick={deselectAll} disabled={selected.size === 0}>Deselect All</Button>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showStockedOnly}
                onChange={e => setShowStockedOnly(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                Show items with inventory only
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Stack>

        <TextField
          size="small"
          placeholder="Filter by name or code…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ mt: 1.5, maxWidth: 360, width: '100%' }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, '@media print': { display: 'none' } }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, '@media print': { display: 'none' } }}>{error}</Alert>
      )}

      {/* ── Print area ─────────────────────────────────────────────────────── */}
      {/*
        Grid math (Letter = 215.9mm × 279.4mm):
          3 columns × 60mm = 180mm  →  margin-left = (215.9 - 180) / 2 = 17.95mm
          7 rows    × 35mm = 245mm  →  margin-top  = 5mm  (bottom = 279.4 - 245 - 5 = 29.4mm)
          Cut lines: 0.25pt solid #888 borders on each label cell; gap:0 so borders touch.
      */}
      <Box
        ref={printRef}
        sx={{
          // On-screen: scrollable grid, labels are smaller for selection
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1,
          '@media print': {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 60mm)',
            gridAutoRows:        '35mm',
            gap:                 0,
            marginLeft:          '17.95mm',
            marginTop:           '5mm',
            width:               '180mm',
            // Necessary for print-color-adjust
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust:       'exact',
          },
        }}
      >
        {filtered.map(label => (
          <LabelCell
            key={label.key}
            label={label}
            selectMode={selectMode}
            selected={selected.has(label.key)}
            onToggle={() => toggleSelect(label.key)}
            onPrintOne={() => handlePrintOne(label)}
          />
        ))}
      </Box>

      {!loading && filtered.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center', '@media print': { display: 'none' } }}>
          No labels found.
        </Typography>
      )}
    </Box>
  );
};

// ── LabelCell ─────────────────────────────────────────────────────────────────

interface LabelCellProps {
  label: LabelItem;
  selectMode: boolean;
  selected: boolean;
  onToggle: () => void;
  onPrintOne: () => void;
}

const LabelCell: React.FC<LabelCellProps> = ({
  label, selectMode, selected, onToggle, onPrintOne,
}) => {
  const locColor   = label.location === 'working' ? '#1565c0' : '#2e7d32';
  const locLabel   = label.location === 'working' ? 'WORKING' : 'STORE ROOM';
  const zeroStock  = label.stock === 0;

  return (
    <Paper
      data-label-key={label.key}
      variant="outlined"
      sx={{
        // ── On-screen ──────────────────────────────────────────────────────
        position:  'relative',
        display:   'flex',
        flexDir:   'row',
        opacity:   zeroStock ? 0.5 : 1,
        overflow:  'hidden',
        cursor:    selectMode ? 'pointer' : 'default',
        outline:   selected ? '2px solid #001550' : 'none',
        p:         0,
        height:    100, // screen preview height
        '&:hover .label-print-btn': { opacity: 1 },

        // ── Print ──────────────────────────────────────────────────────────
        '@media print': {
          width:              '60mm',
          height:             '35mm',
          border:             '0.35mm solid #888',
          borderRadius:       0,
          display:            'flex',
          flexDirection:      'row',
          overflow:           'hidden',
          pageBreakInside:    'avoid',
          breakInside:        'avoid',
          opacity:            1,
          p:                  0,
        },
      }}
      onClick={selectMode ? onToggle : undefined}
    >
      {/* Checkbox overlay (screen only) */}
      {selectMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            '@media print': { display: 'none' },
          }}
        >
          <Checkbox
            checked={selected}
            onChange={onToggle}
            size="small"
            onClick={e => e.stopPropagation()}
          />
        </Box>
      )}

      {/* QR section — left ≈1/3 */}
      <Box
        sx={{
          flexShrink: 0,
          width: { xs: '33%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          bgcolor: '#fff',
          '@media print': {
            width:   '22mm',
            height:  '35mm',
            padding: '1mm',
          },
        }}
      >
        {label.qrDataUrl ? (
          <img
            src={label.qrDataUrl}
            alt={`QR for ${label.materialId} ${label.location}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Box sx={{ width: '100%', aspectRatio: '1', bgcolor: '#eee', borderRadius: 1 }} />
        )}
      </Box>

      {/* Text section — right ≈2/3 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: '4px 6px',
          overflow: 'hidden',
          '@media print': {
            padding: '1.5mm 2mm',
          },
        }}
      >
        {/* Location badge */}
        <Typography
          sx={{
            fontSize: '0.5rem',
            fontWeight: 800,
            letterSpacing: '0.06em',
            color: locColor,
            textTransform: 'uppercase',
            lineHeight: 1.2,
            mb: '1px',
            '@media print': { fontSize: '5pt' },
          }}
        >
          {locLabel}
        </Typography>

        {/* Item name — truncate to 2 lines (bold black, slightly larger for legibility) */}
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#000',
            lineHeight: 1.25,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: '2px',
            '@media print': { fontSize: '7pt', lineHeight: '1.15', WebkitLineClamp: 2 },
          }}
        >
          {label.name}
        </Typography>

        {/* Code (bold black) */}
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontFamily: 'monospace',
            fontWeight: 700,
            color: '#000',
            '@media print': { fontSize: '6.5pt' },
          }}
        >
          {label.materialId}
        </Typography>

        {/* OS-INV-17: per-location Item # — the count-sheet ↔ label key.
            Shown prominently; falls back to legacy numericId when no per-location
            number is assigned yet. */}
        {label.itemNumber != null ? (
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 800,
              color: locColor,
              lineHeight: 1.2,
              '@media print': { fontSize: '8pt' },
            }}
          >
            Item #{String(label.itemNumber).padStart(6, '0')}
          </Typography>
        ) : label.numericId !== undefined ? (
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#000',
              '@media print': { fontSize: '6.5pt' },
            }}
          >
            #{String(label.numericId).padStart(6, '0')}
          </Typography>
        ) : null}

        {/* Zero-stock chip on screen */}
        {zeroStock && (
          <Chip
            label="0 stock"
            size="small"
            sx={{
              height: 16,
              fontSize: '0.45rem',
              mt: '2px',
              bgcolor: '#fff3e0',
              color: '#e65100',
              '@media print': { display: 'none' },
            }}
          />
        )}
      </Box>

      {/* Print-one button (screen only) */}
      {!selectMode && (
        <Tooltip title="Print this label">
          <IconButton
            className="label-print-btn"
            size="small"
            onClick={e => { e.stopPropagation(); onPrintOne(); }}
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.15s',
              bgcolor: 'rgba(255,255,255,0.9)',
              '@media print': { display: 'none' },
            }}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
};
